import type { FreeFormObject, RecipeLabwareDef } from '@ctcells/contract'
import { t } from '@ctcells/i18n'

import type { DeckRackKind } from '../../figures/DeckRackFigure.tsx'

/*
 * **데크 랙 표** — 소모품 요약(`supplySummary`) · 소모품 서랍(`SupplyDetail`)이 같이 읽는 한 벌이다.
 * ⚠ 콘솔 장비 탭(`FocusFigure` 의 `realFigures`)도 읽었으나 그 칸(`pip` · `tc`)이 소모품 판과 겹쳐 걷혀(eric 2026-10-02)
 *   그 길을 2026-10-03 에 지웠다.
 *
 * ## eric 결정 이력 (종전 `DeckSuppliesPanel.tsx` 머리말에서 옮김)
 *
 * - 2026-09-23 「팁 랙 · RC1~3 · RA1~2 · 온도 … 오른쪽에」 → 시료 탭 셋째 열에 데크 판을 세웠다. 「항상 보이게 해줘」
 * - 2026-09-26 「상온 RA1, RA2, RC 이 부분은 빼고, 팁 랙 두 개랑 사진에 보이는 튜브 랙 두 개」 → 베이 · 온도 줄을 걷었다.
 *   ⚠ 되살리지 마라(베이 칸 · 웰 이름 · 시약 이름 줄 · 「현재 / 보관」 온도 줄)
 * - 2026-09-27 「어셈블러의 TC 적재소 그림도 넣어줘. 어셈블러, 그리고 염색기 순으로」 + 냉장 24구
 * - 2026-09-28 「그림 대신 차트로」 → 곧바로 「이거 다시 없애고… 오른쪽은 제거해버려」 ⇒ **시료 탭 오른쪽 판은
 *   걷었다.** 랙 그림은 서랍 · 장비 탭에만 선다. (이후 2026-10-01 회의로 시료별 보기 오른쪽에 소모품 현황 판 `SupplyPanel` 이
 *   섰고, 2026-10-02 에 선택기 줄의 요약 칩 `SupplyStrip` 과 상세 팝오버는 걷혔다 — 소모품은 그 판 한 곳이다.)
 *
 * 🔴 **이름은 비전 화면의 이름을 따른다** — 비전이 왼쪽 위 랙(큰 원 4 + 작은 원 6)을 `tuberack2`, 왼쪽 아래
 *    랙(원 6)을 `tuberack1` 이라 부른다. 팁 랙의 `#1 · #3` 은 검출 순번이라 이름이 아니다 — 차례로 팁 랙 1 · 2.
 */

/**
 * 랙 한 장 — 그림 종류 · **보이는 부품 이름**(`label`) · 접근 이름(`aria`, 한글 사전 키).
 *
 * 🔴 **2026-09-27 — 랙마다 왼쪽 위에 부품 이름.** eric 원문: 「각각의 그림 왼쪽 위에 해당 부품의 명칭을
 * 넣어줘. 예를 들면 tuberack, tiprack 이런 이름 말이야.」 → eric 이 영문 식별자를 예로 줬으니 **영문 소문자**다
 * (한글 문구 규칙의 예외). 비전 화면 어휘(`tuberack2 #0` · `tiprack #1`)의 결을 따르되 검출 순번(`#n`)은 뺐다.
 * 식별자 성격이라 사전 키가 아니라 이 표에 둔다. 스크린리더는 한글 `aria` 만 읽는다(라벨은 `aria-hidden`).
 */
export interface DeckRack {
  kind: DeckRackKind
  label: string
  aria: () => string
  /**
   * 레시피 `payload.labware[].id` — 그 자리의 랩웨어 정의를 찾는 열쇠(eric 2026-09-27 「오픈트론의 랩웨어 …
   * 거기서 가지고 와서 그려」). 레시피 v4.6(로컬 `fe-20260922-full-r1`) 실측 id 다.
   */
  labwareId: string
}

export const DECK: readonly { module: 'asm' | 'stain'; racks: readonly DeckRack[] }[] = [
  {
    module: 'asm',
    /*
     * 레시피 랩웨어 `assembler.tc_stock` — 9칸 T1~T9.
     * 🔴 보이는 이름은 `tc rack`(eric 2026-09-28 「tcstock을 tc rack으로 이름 바꿔주고」). 바뀐 것은 **보이는
     *    글자**뿐이다 — 그림 종류 키 `tcstock` · 소모품 키 · 주소 `/supply/tcstock` · 서버 자리 `assembler.tc_stock` 은
     *    서버 · 주소와 맞물려 그대로다. 한글 접근 이름(「TC 적재소」)도 그대로다
     */
    racks: [
      {
        kind: 'tcstock',
        label: 'TC Rack',
        aria: () => t('monitoring.supply.tcStock'),
        labwareId: 'assembler.tc_stock',
      },
    ],
  },
  {
    module: 'stain',
    /*
     * 비전 화면의 데크를 **통째로 시계 방향 90°** 돌린 배치(eric 2026-09-26) 뒤에 **냉장 24구를 맨 뒤**에
     * 둔다(eric 2026-09-27 「24구짜리를 염색기 부분의 맨 마지막에」). 번호는 비전 이름 그대로(6구 = 튜브 랙 1 · 10구 = 튜브 랙 2).
     */
    racks: [
      {
        kind: 'tubes6',
        label: 'Tube Rack1',
        aria: () => t('monitoring.supply.tubeRack', { index: 1 }),
        labwareId: 'staining.reagent_bay.ambient-1',
      },
      {
        kind: 'tubes4x6',
        label: 'Tube Rack2',
        aria: () => t('monitoring.supply.tubeRack', { index: 2 }),
        labwareId: 'staining.reagent_bay.ambient-2',
      },
      {
        kind: 'tips',
        label: 'Tip Rack1',
        aria: () => t('monitoring.supply.tipRack', { index: 1 }),
        labwareId: 'tips_1000_a',
      },
      {
        kind: 'tips',
        label: 'Tip Rack2',
        aria: () => t('monitoring.supply.tipRack', { index: 2 }),
        labwareId: 'tips_1000_b',
      },
      {
        kind: 'tubes24',
        label: 'Cold Rack',
        aria: () => t('monitoring.supply.chilledRack'),
        labwareId: 'staining.reagent_bay.chilled',
      },
    ],
  },
]

/**
 * 그 모듈 데크의 랙 목록 — **이 표 하나**를 소모품 요약 · 서랍이 같이 읽는다(장비 탭이 읽던 길은 2026-10-03 에 걷었다).
 * 표를 두 벌로 두지 마라 — 랙이 더해지거나 이름이 바뀔 때 그리는 곳끼리 갈린다.
 */
export function deckRacksOf(module: 'asm' | 'stain'): readonly DeckRack[] {
  return DECK.find((entry) => entry.module === module)?.racks ?? []
}

/**
 * **랩웨어 id 의 대조 열쇠** — 인스턴스 접미의 점(`ambient.1`)과 하이픈(`ambient-1`)을 같은 것으로 본다.
 *
 * 🔴 **왜**(2026-10-11 dev2 실측 · 쵸단): 정본 개명으로 새 레시피 · 모듈 장부(`consumables.reagents_by_well`) · 레시피 판
 *    (`Batch.reagent_basis`)이 전부 하이픈(`staining.reagent_bay.ambient-1`)인데, 이 표의 튜브 랙 둘이 점(`ambient.1`)으로 박혀
 *    있었고 대조가 정확 일치라 **실물에서 튜브 랙 1·2 의 장부 잔량 · 레시피 정의가 하나도 안 붙었다**(냉장 랙 `chilled` 은 접미가 없어 무사).
 *    옛 커밋 레시피는 여전히 점 꼴이라 둘 다 받아야 한다.
 * 🔴 **랩웨어 id 를 맞대는 곳은 전부 이것을 쓴다** — `definitionAt` · `deckOccupancy.wellVolume` · `deckOccupancy.deckReagentsAt` ·
 *    `supplySummary` 의 시약대 ↔ 랙 대조. 한 곳만 정확 일치로 남으면 그 자리가 같은 병을 다시 앓는다.
 * ⚠ 접미 «번호»는 그대로 가른다 — `ambient-1` 과 `ambient-2` 는 다른 랙이다.
 */
export function labwareKey(labwareId: string): string {
  return labwareId.replace(/\.(\d+)$/u, '-$1')
}

/** 두 랩웨어 id 가 같은 자리인가(`labwareKey` 로 맞댄다) */
export function sameLabware(a: string | null | undefined, b: string | null | undefined): boolean {
  return typeof a === 'string' && typeof b === 'string' && labwareKey(a) === labwareKey(b)
}

/**
 * **그 자리의 랩웨어 정의** — 도는 작업의 레시피 `payload.labware[]` 에서 `id` 로 찾는다(접미 점 · 하이픈은 같은 것 · `labwareKey`). 없으면 `undefined`
 * (그림이 번들 사본으로 선다 · `DeckRackFigure`). 서버가 준 객체를 그대로 넘긴다 — 키를 고르지 않는다.
 */
export function definitionAt(
  labware: readonly RecipeLabwareDef[] | undefined,
  labwareId: string,
): FreeFormObject | undefined {
  const found = labware?.find((entry) => sameLabware(entry.id, labwareId))
  return found === undefined ? undefined : Object.fromEntries(Object.entries(found))
}
